import React, { useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { paymentMethods } from 'constants/dropdownOptions';
import { ModalHeader } from 'shared/ModalHeader';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { useUserDetailsContext } from 'context/userDetailsContext';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { computeTotalRefundAmount } from './refundPaymentUtils';
import { useRefundPaymentV2 } from './hooks/useRefundPaymentV2';

export const RefundPaymentV2 = (props) => {
  const userContext = useUserDetailsContext();
  const {
    amount,
    setAmount,
    trustRefundAmount,
    earnedRefundAmount,
    fullRefund,
    onChangeRefundFull,
    refundDate,
    setRefundDate,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    notes,
    setNotes,
    formErrors,
    clearFieldError,
    loading,
    recordDetails,
    isSplitRefund,
    onSubmit,
    onTrustRefundAmountChange,
    onEarnedRefundAmountChange,
  } = useRefundPaymentV2({
    selectedRecord: props.selectedRecord,
    isLeadSpecific: props.isLeadSpecific,
    onSuccess: props.onSuccess,
  });

  const displayedTotalRefundAmount = useMemo(
    () => computeTotalRefundAmount(trustRefundAmount, earnedRefundAmount),
    [earnedRefundAmount, trustRefundAmount]
  );

  return (
    <Dialog header={ModalHeader} visible={props.show} onHide={props.onHide} className="create-msg-popup" closable={false}>
      <div className="row">
        <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
          <div className="modal-heading F-size16 text-bold">Request Refund</div>
        </div>

        {isSplitRefund ? (
          <>
            <div className="d-flex align-items-top py-2 flex-wrap">
              <div className="col-12 col-sm-4 pt-2">Trust Refund Amount</div>
              <div className="col-12 col-sm-8">
                <InputNumber
                  inputId="trust-refund-amount"
                  aria-label="Trust Refund Amount"
                  value={trustRefundAmount}
                  onChange={(e) => onTrustRefundAmountChange(e.value)}
                  onValueChange={(e) => onTrustRefundAmountChange(e.value)}
                  className="input-shadow w-100"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                />
                {formErrors.trust_refund_amount && <span className="text-danger">{formErrors.trust_refund_amount}</span>}
              </div>
            </div>

            <div className="d-flex align-items-top py-2 flex-wrap">
              <div className="col-12 col-sm-4 pt-2">Earned Refund Amount</div>
              <div className="col-12 col-sm-8">
                <InputNumber
                  inputId="earned-refund-amount"
                  aria-label="Earned Refund Amount"
                  value={earnedRefundAmount}
                  onChange={(e) => onEarnedRefundAmountChange(e.value)}
                  onValueChange={(e) => onEarnedRefundAmountChange(e.value)}
                  className="input-shadow w-100"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                />
                {formErrors.earned_refund_amount && <span className="text-danger">{formErrors.earned_refund_amount}</span>}
              </div>
            </div>

            <div className="d-flex align-items-top py-2 flex-wrap">
              <div className="col-12 col-sm-4 pt-2">
                Total Refund Amount
                <span className="text-danger">*</span>
              </div>
              <div className="col-12 col-sm-8">
                <InputNumber
                  inputId="total-refund-amount"
                  aria-label="Total Refund Amount"
                  value={displayedTotalRefundAmount}
                  className="input-shadow w-100"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  readOnly
                  inputClassName="p-disabled"
                />
                {formErrors.total_refund_amount && <span className="text-danger">{formErrors.total_refund_amount}</span>}
                <div className="mt-3">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={fullRefund}
                      name="refund-full-split"
                      onChange={(e) => onChangeRefundFull(e.target.checked)}
                    />
                    <span className="slider round"></span>
                  </label>
                  <span className="ms-2">Refund full amount</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="d-flex align-items-top py-2 flex-wrap">
            <div className="col-12 col-sm-4 pt-2">
              Refund Amount
              <span className="text-danger">*</span>
            </div>

            <div className="col-12 col-sm-8">
              <InputNumber
                inputId="refund-amount"
                aria-label="Refund Amount"
                value={amount}
                onValueChange={(e) => {
                  setAmount(e.value);
                  clearFieldError('amount');
                }}
                className="input-shadow w-100"
                mode="currency"
                currency="USD"
                locale="en-US"
              />
              {formErrors.amount && <span className="text-danger">{formErrors.amount}</span>}
              <div className="mt-3">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={fullRefund}
                    name="refund-full-legacy"
                    onChange={(e) => onChangeRefundFull(e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
                <span className="ms-2">Refund full amount</span>
              </div>
            </div>
          </div>
        )}

        <div className="d-flex align-items-center py-2">
          <div className="col-12 col-sm-4 p-medium">
            Payment Method
            <span className="text-danger">*</span>
          </div>
          <div className="col-12 col-sm-8">
            <Dropdown
              className="input-shadow w-100 p-0"
              options={paymentMethods}
              value={selectedPaymentMethod}
              onChange={(e) => {
                setSelectedPaymentMethod(e.value);
                clearFieldError('payment_method');
              }}
              filter
              disabled={recordDetails?.is_confido_transaction}
            />
            {formErrors.payment_method && <span className="text-danger">{formErrors.payment_method}</span>}
          </div>
        </div>
        <div className="d-flex align-items-center py-2">
          <div className="col-12 col-sm-4">
            Date
            <span className="text-danger">*</span>
          </div>
          <div className="col-12 col-sm-8">
            <TimezoneDatePicker
              value={refundDate}
              onChange={(value) => {
                setRefundDate(value);
                clearFieldError('refundDate');
              }}
              className="input-shadow w-100"
              timezone={userContext?.userDetails?.timezone}
              isEndOfDay={true}
              disabled={props.selectedRecord?.is_confido_transaction}
            />
            {formErrors.refundDate && <span className="text-danger">{formErrors.refundDate}</span>}
          </div>
        </div>
        <div className="d-flex align-item-center py-2">
          <div className="col-12 col-sm-4">Notes</div>
          <div className="col-12 col-sm-8">
            <TextSnippetWrapper
              type="textarea"
              value={notes}
              onChange={(e) => {
                if (e.target.value.length <= 255) {
                  setNotes(e.target.value);
                }
              }}
              maxLength={255}
              className="input-shadow w-100 p-2"
              autoComplete="off"
            />
            <small className="text-muted d-block text-end">{notes?.length || 0}/255</small>
          </div>
        </div>
        {recordDetails?.is_confido_transaction && (
          <div className="d-flex align-items-start py-2">
            <i className="pi pi-info-circle text-primary me-2 mt-1" style={{ fontSize: '0.9rem' }} />
            <small className="text-muted">
              Note: Any surcharge from the original payment will be refunded proportionately with the refund amount.
            </small>
          </div>
        )}
      </div>
      <hr></hr>
      <div className="row">
        <div className="col-12 d-flex justify-content-end mt-2">
          <Button className="p-button-secondary outline" label="Cancel" onClick={props.onHide} />
          <Button className="p-button-secondary ms-2" label="Refund" onClick={onSubmit} loading={loading} />
        </div>
      </div>
    </Dialog>
  );
};
